import * as THREE from 'three';
import { Enemy, type DropTableEntry } from './Enemy.js';
import type { Game } from '../../game/Game.js';
import type { Circle } from '../../systems/Collision.js';
import { Projectile } from '../Projectile.js';

/**
 * Heavy drop table.
 *
 * From design doc:
 * - Core drops: common (50%)
 *   - Standard: 20%
 *   - Capacitor: 15%
 *   - Bulwark: 15%
 * - Transport drops: occasional (30%)
 *   - Treads: 15%
 *   - Hover: 8%
 *   - Legs: 7%
 * - No drop: 20%
 */
const HEAVY_DROPS: DropTableEntry[] = [
  { partId: 'standard', weight: 15 },
  { partId: 'capacitor', weight: 12 },
  { partId: 'bulwark', weight: 12 },
  { partId: 'treads', weight: 12 },
  { partId: 'hover', weight: 6 },
  { partId: 'legs', weight: 5 },
  { partId: 'mortar', weight: 8 },   // Heavy weapon fits heavy enemy
  { partId: 'lancer', weight: 5 },
  { partId: 'splitter', weight: 5 },
  { partId: null, weight: 20 }, // No drop
];

/**
 * Heavy enemy - slow, armored tank.
 *
 * Behavior:
 * - Slowly moves toward player
 * - Fires slow, heavy projectiles periodically
 * - High HP - tank enemy
 *
 * Visual:
 * - Orange/red color
 * - Larger, bulkier
 * - Box-like or spherical body
 * - Size: ~1.2 units
 *
 * Stats:
 * - HP: 100 (tank)
 * - Speed: 1.5 units/sec (slow)
 * - Attack damage: 35 (high)
 * - Attack range: 8 units (ranged)
 * - Attack rate: 0.5 attacks/sec (slow)
 * - Projectile speed: 6 units/sec (slow but heavy)
 */
export class Heavy extends Enemy {
  /** Movement speed in units per second */
  private readonly speed: number = 1.5;

  /** Damage dealt per projectile */
  private readonly attackDamage: number = 35;

  /** Time until next shot can be fired (seconds) */
  private attackCooldown: number = 0;

  /** How far the heavy can detect and shoot at the player */
  private readonly attackRange: number = 8;

  /** Attacks per second */
  private readonly attackRate: number = 0.5;

  /** Projectile speed in units per second */
  private readonly projectileSpeed: number = 6;

  /** Collision radius for hitbox */
  private readonly collisionRadius: number = 0.8;

  /** Telegraph duration before firing (seconds) - longer for heavy */
  private readonly telegraphDuration: number = 0.5;

  /** Whether ready to fire after telegraph */
  private readyToFire: boolean = false;

  /** Reference to barrel mesh for glow effect */
  private barrelMesh: THREE.Mesh | null = null;

  constructor(x: number, y: number, game: Game) {
    super(x, y, 100, HEAVY_DROPS, game);
    this.damage = this.attackDamage; // Set base damage for scaling
    this.createMesh();
  }

  /**
   * Create the heavy's visual mesh.
   *
   * Visual structure:
   * - Large, bulky body
   * - Box-like structure for tank appearance
   * - Orange/red enemy color
   */
  private createMesh(): void {
    const group = new THREE.Group();

    // Main body - large box
    const bodyGeometry = new THREE.BoxGeometry(1.2, 0.8, 1.2);
    const bodyMaterial = new THREE.MeshStandardMaterial({
      color: 0xff5722, // Orange-red enemy color
      metalness: 0.6,
      roughness: 0.5,
    });
    const body = new THREE.Mesh(bodyGeometry, bodyMaterial);
    body.position.y = 0.4; // Center at half height
    group.add(body);

    // Armor plating - darker accents
    const plateGeometry = new THREE.BoxGeometry(1.0, 0.2, 1.0);
    const plateMaterial = new THREE.MeshStandardMaterial({
      color: 0xe64a19, // Darker orange-red
      metalness: 0.7,
      roughness: 0.4,
    });
    const topPlate = new THREE.Mesh(plateGeometry, plateMaterial);
    topPlate.position.y = 0.85; // On top
    group.add(topPlate);

    const bottomPlate = new THREE.Mesh(plateGeometry, plateMaterial);
    bottomPlate.position.y = 0.05; // Near ground
    group.add(bottomPlate);

    // Weapon barrel - cylinder pointing forward
    const barrelGeometry = new THREE.CylinderGeometry(0.15, 0.15, 0.8, 8);
    const barrelMaterial = new THREE.MeshStandardMaterial({
      color: 0x424242, // Dark gray
      emissive: 0x000000, // Start with no emissive
      metalness: 0.7,
      roughness: 0.4,
    });
    this.barrelMesh = new THREE.Mesh(barrelGeometry, barrelMaterial);
    this.barrelMesh.rotation.z = Math.PI / 2; // Point horizontally
    this.barrelMesh.position.set(0.7, 0.5, 0); // Offset from center, mid-height
    group.add(this.barrelMesh);

    this.mesh = group;
    this.updateMeshTransform();
  }

  update(dt: number): void {
    const dtSec = dt / 1000;

    // Update telegraph animation
    const telegraphComplete = this.updateTelegraph(dtSec);
    if (telegraphComplete) {
      this.readyToFire = true;
    }

    // Add barrel glow effect during telegraph
    if (this.isTelegraphing && this.barrelMesh) {
      const t = this.telegraphTimer / this.telegraphDuration;
      const glowIntensity = (1 - t) * 2; // Intensify as we get closer to firing
      const barrelMaterial = this.barrelMesh.material as THREE.MeshStandardMaterial;
      barrelMaterial.emissive.setHex(0xff5722); // Orange glow
      barrelMaterial.emissiveIntensity = glowIntensity;
    } else if (this.barrelMesh) {
      // Reset barrel glow
      const barrelMaterial = this.barrelMesh.material as THREE.MeshStandardMaterial;
      barrelMaterial.emissive.setHex(0x000000);
      barrelMaterial.emissiveIntensity = 0;
    }

    // Decrement attack cooldown
    if (this.attackCooldown > 0) {
      this.attackCooldown -= dtSec;
    }

    // Find the player
    const player = this.findPlayer();
    if (!player) {
      return;
    }

    // Calculate distance to player
    const dx = player.position.x - this.position.x;
    const dy = player.position.y - this.position.y;
    const dist = Math.sqrt(dx * dx + dy * dy);

    // If player is in range, aim and shoot
    if (dist <= this.attackRange) {
      // Face player
      this.rotation = Math.atan2(dy, dx);

      // Start telegraph when ready to shoot
      if (this.attackCooldown <= 0 && !this.isTelegraphing && !this.readyToFire) {
        this.startTelegraph(this.telegraphDuration);
      }

      // Fire when telegraph completes
      if (this.readyToFire) {
        this.fireAtPlayer(player.position.x, player.position.y);
        this.attackCooldown = 1 / this.attackRate;
        this.readyToFire = false;
      }
    }

    // Slowly move toward player
    this.moveTowardPlayer(dtSec);

    this.updateMeshTransform();
  }

  /**
   * Move toward the player.
   *
   * @param dtSec - Delta time in seconds
   */
  private moveTowardPlayer(dtSec: number): void {
    const player = this.findPlayer();
    if (!player) return;

    const dx = player.position.x - this.position.x;
    const dy = player.position.y - this.position.y;
    const dist = Math.sqrt(dx * dx + dy * dy);

    if (dist > 0) {
      // Normalize direction
      const nx = dx / dist;
      const ny = dy / dist;

      // Move toward player (slowly)
      this.position.x += nx * this.speed * dtSec;
      this.position.y += ny * this.speed * dtSec;

      // Clamp to room bounds
      const room = this.game.getRoom();
      this.position = room.clampPosition(this.position, this.collisionRadius);
    }
  }

  /**
   * Fire a heavy projectile at the player.
   *
   * @param targetX - Player X position
   * @param targetY - Player Y position
   */
  private fireAtPlayer(targetX: number, targetY: number): void {
    const dx = targetX - this.position.x;
    const dy = targetY - this.position.y;
    const angle = Math.atan2(dy, dx);

    // Create projectile
    const projectile = new Projectile(
      this.position.x,
      this.position.y,
      angle,
      this.projectileSpeed,
      this.damage, // Use base damage (may be scaled)
      4 // Projectile lifetime in seconds (slightly longer range)
    );

    // Set owner to prevent self-collision
    projectile.setOwner(this);

    // Set room for wall collision
    const room = this.game.getRoom();
    projectile.setRoom(room);

    // Change projectile color to darker red/orange for heavy projectiles
    if (projectile.mesh instanceof THREE.Mesh) {
      const material = projectile.mesh.material as THREE.MeshStandardMaterial;
      material.color.setHex(0xd84315); // Dark orange-red
      material.emissive.setHex(0xd84315);
    }

    // Set impact effect callback
    projectile.setOnDestroy((x, y) => {
      this.game.spawnImpactEffect(x, y, 0xd84315); // Dark orange impact for heavy projectiles
    });

    this.game.addEntity(projectile);
  }

  /**
   * Find the player entity in the game.
   *
   * Returns null if no player found.
   */
  private findPlayer(): { position: { x: number; y: number } } | null {
    return (this.game as any).playerMech || null;
  }

  getCollisionShape(): Circle {
    return {
      x: this.position.x,
      y: this.position.y,
      radius: this.collisionRadius,
    };
  }
}
